import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../utils/translations';
import { Zap, BookOpen, Package, Shield, Award } from 'lucide-react';

const Wingfoiling = () => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);

  return (
    <div className="min-h-screen" data-testid="wingfoiling-page">
      {/* Hero with Image */}
      <section className="relative h-[60vh] overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1666175497932-8c88bbad53b1?crop=entropy&cs=srgb&fm=jpg&q=85" 
            alt="Wingfoiling"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-orange-900/80 to-yellow-900/70"></div>
        </div>
        <div className="relative h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
            <Zap className="w-20 h-20 mx-auto mb-6 text-yellow-400" />
            <h1 className="text-5xl md:text-6xl font-bold mb-6">{t('wingfoiling.title')}</h1>
            <p className="text-2xl text-orange-100 max-w-2xl mx-auto">{t('wingfoiling.subtitle')}</p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center max-w-3xl mx-auto">
            <p className="text-lg text-gray-600 leading-relaxed">{t('wingfoiling.description')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
            <div className="group bg-gradient-to-br from-orange-50 to-yellow-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <BookOpen className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-3">{t('wingfoiling.learn')}</h3>
              <p className="text-gray-600 text-lg">{t('wingfoiling.learn_desc')}</p>
            </div>
            <div className="group bg-gradient-to-br from-yellow-50 to-amber-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-amber-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Package className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-3">{t('wingfoiling.gear')}</h3>
              <p className="text-gray-600 text-lg">{t('wingfoiling.gear_desc')}</p>
            </div>
          </div>

          {/* Detailed Information Sections */}
          <div className="space-y-16">
            {/* About Wing Foil */}
            <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-3xl p-10 md:p-12">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Zap className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold mb-4">{t('wingfoiling.about_title')}</h2>
                  <p className="text-lg text-gray-700 leading-relaxed">{t('wingfoiling.about_desc')}</p>
                </div>
              </div>
            </div>

            {/* Our School */}
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-3xl p-10 md:p-12">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold mb-4">{t('wingfoiling.school_title')}</h2>
                  <p className="text-lg text-gray-700 leading-relaxed">{t('wingfoiling.school_desc')}</p>
                </div>
              </div>
            </div>

            {/* Our Courses */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl p-10 md:p-12">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Award className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold mb-4">{t('wingfoiling.courses_title')}</h2>
                  <p className="text-lg text-gray-700 leading-relaxed">{t('wingfoiling.courses_desc')}</p>
                </div>
              </div>
            </div>

            {/* Safety */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-10 md:p-12">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold mb-4">{t('wingfoiling.safety_title')}</h2>
                  <p className="text-lg text-gray-700 leading-relaxed">{t('wingfoiling.safety_desc')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Image Gallery */}
          <div className="mt-16 grid grid-cols-2 gap-4">
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img 
                src="https://customer-assets.emergentagent.com/job_surfapp-manager/artifacts/8rpytn7d_gallerie4.jpg" 
                alt="Wingfoiling Gallery"
                className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img 
                src="https://customer-assets.emergentagent.com/job_surfapp-manager/artifacts/fdf6umrj_IMG_5660-scaled.jpg" 
                alt="Wingfoiling Action"
                className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Wingfoiling;